/**
 * Read amounts out of a response **as digit strings**, never as `Number`s.
 *
 * `JSON.parse` turns every JSON integer literal into a `number`, which silently
 * rounds above 2^53 — by the time the console could look at `balance`, the
 * digits would already be gone. So the raw response text is transformed at the
 * token level first: every integer literal whose key is an amount key is quoted,
 * and only then parsed. Nothing on this path constructs a `Number` from an
 * amount.
 *
 * The transform is a real scanner (it tracks string literals and escapes), not
 * a regex over the whole document, so a colon or a key-shaped substring inside a
 * string value cannot be mistaken for a key.
 */

/** Response keys whose JSON integer literal must survive as digits. */
export const AMOUNT_KEYS: ReadonlySet<string> = new Set([
  'balance',
  'available',
  'pending',
  'amount',
  'fee',
  'balance_before',
  'balance_after',
  'requested',
  'total_reversed',
  'shortfall',
])

function readStringToken(text: string, start: number): { value: string; end: number } | null {
  // text[start] === '"'
  let i = start + 1
  let value = ''
  while (i < text.length) {
    const ch = text[i]
    if (ch === '\\') {
      value += text[i + 1] ?? ''
      i += 2
      continue
    }
    if (ch === '"') return { value, end: i + 1 }
    value += ch
    i += 1
  }
  return null
}

/**
 * Quote the integer literals of every amount key in `text`, leaving the rest of
 * the document byte-identical.
 */
export function quoteAmountLiterals(text: string): string {
  let out = ''
  let i = 0
  while (i < text.length) {
    const ch = text[i]
    if (ch !== '"') {
      out += ch
      i += 1
      continue
    }
    const token = readStringToken(text, i)
    if (!token) {
      out += text.slice(i)
      break
    }
    const literal = text.slice(i, token.end)
    out += literal
    i = token.end

    // Is this string a key whose value is an amount?
    let j = i
    while (j < text.length && /\s/.test(text[j])) j += 1
    if (text[j] !== ':' || !AMOUNT_KEYS.has(token.value)) continue

    out += text.slice(i, j + 1)
    i = j + 1
    while (i < text.length && /\s/.test(text[i])) {
      out += text[i]
      i += 1
    }
    const numberMatch = /^-?\d+/.exec(text.slice(i))
    if (!numberMatch) continue
    out += `"${numberMatch[0]}"`
    i += numberMatch[0].length
  }
  return out
}

/**
 * Parse a JSON response with every amount key carried as a digit string.
 *
 * `null` amounts stay `null` (an absent `balance_before` is not a zero), and
 * every non-amount field keeps its ordinary JSON type.
 */
export function parseJsonPreservingAmounts<T>(text: string): T {
  return JSON.parse(quoteAmountLiterals(text)) as T
}
